'use client';

import styled from 'styled-components';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';

// Closing row of the TripAdvisor reviews content (MPH-763): left-aligned on mobile, right-aligned
// from tablet up.
const Row = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 16px;

  @media ${customCss.breakpoints.sm} {
    justify-content: flex-end;
  }
`;

// Product wants this to read as plain text rather than a link (no accent colour, no underline). It
// stays a real anchor for semantics and keyboard users; hover/focus bring the underline back so the
// affordance is still discoverable.
const PlainTextLink = styled.a`
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

type TripAdvisorHotelPageLinkProps = {
  /** Hotel review page on tripadvisor.com, already resolved (see getTripAdvisorHotelReviewsUrl). */
  href: string;
  /** Locale-formatted total review count shown in the copy, e.g. "1,234". */
  reviewCountFormatted: string;
  /** Desktop opens TripAdvisor in a new tab; mobile navigates in the same tab. */
  isMobile: boolean;
};

export default function TripAdvisorHotelPageLink({
  href,
  reviewCountFormatted,
  isMobile,
}: TripAdvisorHotelPageLinkProps) {
  const { t } = useTranslation();
  const label =
    t('vacation.select.details.guest.reviews.tripadvisor.see_all', { count: reviewCountFormatted }) ||
    `See all ${reviewCountFormatted} reviews on Tripadvisor`;

  return (
    <Row>
      <PlainTextLink href={href} {...(isMobile ? {} : { target: '_blank', rel: 'noopener noreferrer' })}>
        {label}
      </PlainTextLink>
    </Row>
  );
}
